import React, {useEffect, useRef} from 'react'
import {Layer, Rect, Transformer} from 'react-konva'
import {
    actionChangeSelectedLayer,
    updateLayerAttribute,
    actionToggleCropMode,
    actionApplyCustomShapeLayers,
} from '../../context/SelectedVariantContext/action'
import {handleCalculateDpi} from '../../context/SelectedVariantContext/helper'
import BackgroundLayer from './BackgroundLayer'
import SafeZoneLayer from './SafeZoneLayer'
import TextLayer from './TextLayer'
import ImageLayer from './ImageLayer'
import ShapeLayer from './ShapeLayer'
import {debounce} from 'lodash'
import {STORAGE_KEYS} from '../../../custom-shape/constants/storageKeys'
import DraftPreviewStore from '../../../custom-shape/persistence/DraftPreviewStore'
import {mapNodesToLayers} from '../shapes/mapNodesToLayers'
import {calcScale} from '../../../../helpers/shapes/calcScale'
import {hasFeatureTag} from '../../../../helpers/settings'
import ConditionalRender from '../../../../shared/ConditionalRender'

const MockupLayers = ({
    background,
    layers,
    draggable,
    dispatch,
    selectedLayer,
    selection,
    rectRefs,
    transformerRef,
    cropMode,
    editorWrapper,
    productCreatedType,
    selectedSide,
    side,
    loadingImageLayer,
}) => {
    const layerRef = useRef(null)
    const mappingAppliedRef = useRef(false)
    
    // Consume mapping payload from create-custom-shape on return
    useEffect(() => {
        if (mappingAppliedRef.current || !background) return
        if (!hasFeatureTag('custom_shape_step2')) return

        let raw
        try {
            raw = localStorage.getItem(STORAGE_KEYS.CUSTOM_SHAPE_MAPPING_PAYLOAD)
        } catch {
            return
        }
        if (!raw) return

        let payload
        try {
            payload = JSON.parse(raw)
        } catch {
            return
        }

        mappingAppliedRef.current = true

        // Clean up localStorage immediately to prevent re-application
        try {
            localStorage.removeItem(STORAGE_KEYS.CUSTOM_SHAPE_MAPPING_PAYLOAD)
        } catch {
            // ignore
        }

        const {draftId, document, bounds} = payload
        if (!document?.nodes?.length) return

        // Step 1: Load silhouette preview as safe zone image
        const applySafeZoneAndLayers = async () => {
            let safeZoneScale = {scaleX: 1, scaleY: 1}
            let safeZoneImage = null
            if (draftId) {
                try {
                    const record = await DraftPreviewStore.load(draftId)
                    if (record?.blob) {
                        const url = URL.createObjectURL(record.blob)
                        const {width: bgWidth = 1000, height: bgHeight = 1000} = background
                        const size = Math.min(bgWidth, bgHeight)

                        // Load image to get actual dimensions for correct scale calculation
                        const img = new Image()
                        img.src = url
                        await new Promise((resolve) => {
                            img.onload = resolve
                        })

                        const scale = calcScale({width: img.naturalWidth, height: img.naturalHeight}, size, true)

                        safeZoneScale = scale

                        safeZoneImage = {
                            src: url,
                            ...scale,
                        }
                    }
                } catch {
                    // Best-effort: silhouette may not exist
                }
            }

            // Step 2: Map nodes to layers
            const mappedLayers = await mapNodesToLayers(document, background, bounds, safeZoneScale)

            if (mappedLayers.length > 0 || safeZoneImage) {
                dispatch(
                    actionApplyCustomShapeLayers({
                        layers: mappedLayers,
                        safeZoneImage,
                    }),
                )
            }
        }

        applySafeZoneAndLayers()
    }, [background, dispatch])

    return (
        <>
            <Layer ref={layerRef} listening={!draggable}>
                {/* Background */}
                <BackgroundLayer
                    onClick={() => dispatch(actionChangeSelectedLayer())}
                    imageProps={background}
                    isBackground={true}
                    zIndexDefault={0}
                />
                {/* Background color */}
                <BackgroundLayer
                    onClick={() => dispatch(actionChangeSelectedLayer())}
                    imageProps={background}
                    zIndexDefault={1}
                />
                {/* {
                    productCreatedType === 'regularNoTemplate' && (
                        <Rect
                            x={editorWrapper.width / 2 - 230}
                            y={editorWrapper.height / 2 - 230}
                            width={460}
                            height={460}
                            fill="white"
                            shadowBlur={3}
                        />
                    )
                } */}
                {(productCreatedType === 'regularHasTemplate' || productCreatedType === 'combineLayout') && (
                    <Rect
                        x={0}
                        y={0}
                        width={1000}
                        height={1000}
                        fill="#ffffff"
                        stroke="#D8DBDE"
                        strokeWidth={20}
                        {...background}
                    />
                )}
                {layers?.map((layer, i) => {
                    const isSelected = layer.id === selectedLayer?.id
                    const props = {
                        key: layer.id,
                        zIndexDefault: i + 2,
                        isSelected,
                        onChange: (layer, updateHistory = true) => {
                            dispatch(updateLayerAttribute(layer, updateHistory))
                        },
                        onSelect: debounce(() => {
                            if (!isSelected) {
                                dispatch(actionChangeSelectedLayer(layer))
                                dispatch(actionToggleCropMode(false))
                            }
                        }, 100),
                        background,
                        rectRefs, // Pass rectRefs to layers
                        layerId: layer.id, // Pass layer ID for registration
                        transformerRef, // Pass transformerRef
                    }

                    if (layer.layerType === 'text') {
                        return <TextLayer {...props} textProps={layer} />
                    }
                    if (layer.layerType === 'shape') {
                        return <ShapeLayer {...props} shapeProps={layer} />
                    }
                    return (
                        <ImageLayer
                            {...props}
                            side={side}
                            selectedSide={selectedSide}
                            imageProps={layer}
                            dispatch={dispatch}
                            onChangeSizeImage={debounce((newSize) => {
                                let newAttrs = {...layer, ...newSize}
                                if (layer.imageType !== 'image/svg+xml') {
                                    const dpi = handleCalculateDpi(newAttrs)
                                    newAttrs = {...newAttrs, dpi}
                                    dispatch(actionChangeSelectedLayer(newAttrs))
                                }
                            }, 100)}
                            cropMode={cropMode}
                            productCreatedType={productCreatedType}
                            loadingImageLayer={loadingImageLayer}
                        />
                    )
                })}

                {/* Selection rectangle for drag selection */}
                {selection?.visible && (
                    <Rect
                        x={selection.x}
                        y={selection.y}
                        width={selection.width}
                        height={selection.height}
                        fill="rgba(0,123,255,0.2)"
                        stroke="rgba(0,123,255,0.8)"
                        strokeWidth={1}
                        listening={false}
                        dash={[5, 5]}
                    />
                )}

                <Transformer
                    ref={transformerRef}
                    enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
                    // resizeEnabled={false}
                    rotateEnabled={false}
                    flipEnabled={false}
                    anchorCornerRadius={30}
                    rotateAnchorOffset={20}
                    anchorStrokeWidth={2}
                    draggable={false}
                    boundBoxFunc={(oldBox, newBox) => {
                        return newBox
                    }}
                />

                {/* SafeZone */}
                <ConditionalRender
                    fallback={
                        <BackgroundLayer
                            onClick={() => dispatch(actionChangeSelectedLayer())}
                            imageProps={background}
                            showImage={true}
                            name="background"
                        />
                    }
                    condition={hasFeatureTag('custom_shape_step2')}
                >
                    <SafeZoneLayer onClick={() => dispatch(actionChangeSelectedLayer())} />
                </ConditionalRender>
            </Layer>
        </>
    )
}

export default MockupLayers
